import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Classifier,
  ExactValuesWithYearPeriodAndRange,
  ValuesWithYearPeriodAndYearRangeFilter,
} from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import InputAutocomplete, { AutocompleteOption } from '../../Inputs/Autocomplete';
import {
  formatClassifiersToOptions,
  mapSelectedAutocompleteOptions,
} from '../../Inputs/Autocomplete/autocomplete.utils';
import {
  InputDateRangeComponentWithDropdown,
  YEAR_DROPDOWN_OPTIONS,
} from '../../Inputs/DateRangeWithDropdown';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import { allowedYearRange } from '../../Inputs/YearRange/YearRange';

interface Props {
  root?: boolean;
  active: boolean;
  defaultCriteria?: string[]; // default dropdown value, NOT default state
  criteriaOptions?: any[];
  onChange: (values: ValuesWithYearPeriodAndYearRangeFilter) => void;
  defaultAllowedYearRange?: typeof allowedYearRange;
  defaultValue?: ValuesWithYearPeriodAndYearRangeFilter;
  items?: Classifier[];
}

const ValuesWithYearPeriodAndYearRangeFilterInput: React.FC<Props> = React.memo(
  ({
    defaultCriteria = [YEAR_DROPDOWN_OPTIONS[1].value],
    criteriaOptions = YEAR_DROPDOWN_OPTIONS,
    onChange,
    root,
    active,
    defaultAllowedYearRange = allowedYearRange,
    defaultValue = {
      value: {
        values: [],
        criteria: defaultCriteria[0] as ExactValuesWithYearPeriodAndRange.CriteriaEnum,
        years: defaultAllowedYearRange,
      },
    },
    items = [],
  }) => {
    const { t } = useTranslation();
    const [state, setState] = useState<ValuesWithYearPeriodAndYearRangeFilter>(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const options: AutocompleteOption[] = useMemo(() => formatClassifiersToOptions(items), []);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    function getCriteria(criteria: ExactValuesWithYearPeriodAndRange.CriteriaEnum) {
      const label = criteriaOptions.find((c) => c.value === criteria)?.label;

      return label ? t(label) : '';
    }

    function getValues(values: string[]) {
      const valuesList = values.map((v) => options.find((o) => o.value === v)?.label);

      return valuesList.join(', ');
    }

    const rs: any[] = [];
    if (state.value && (state.value.values ?? []).length > 0) {
      rs.push({ label: 'Value:', value: getValues(state.value.values ?? []) });
    }
    if (state.value && state.value.criteria) {
      rs.push({ label: 'Applies for:', value: getCriteria(state.value.criteria) });
    }
    if (state.value && state.value.years?.from && state.value.years?.to) {
      rs.push({
        label: 'Range:',
        value: `From ${state.value.years.from} to ${state.value.years.to}`,
      });
    }

    return (
      <>
        {active ? (
          <>
            <InputAutocomplete
              options={options}
              defaultValue={mapSelectedAutocompleteOptions(options, state.value?.values)}
              onChange={(values) => {
                setState({
                  ...state,
                  value: {
                    ...state.value,
                    values: [...values.map((value) => value.value)],
                  },
                });
              }}
            />
            <InputDateRangeComponentWithDropdown
              active={active}
              defaultOptions={criteriaOptions}
              allowedYearRange={defaultAllowedYearRange}
              defaultState={{ criteria: state.value?.criteria, years: state.value?.years }}
              defaultValue={[
                state?.value?.criteria as ExactValuesWithYearPeriodAndRange.CriteriaEnum,
              ]}
              onChange={(values) => {
                setState({
                  ...state,
                  value: {
                    ...state.value,
                    criteria: values.criteria,
                    years: values.years,
                  },
                });
              }}
            />
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

export default ValuesWithYearPeriodAndYearRangeFilterInput;
